import { ErrorModal } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import { makeStyles } from "@mui/styles";
import Alert from "@mui/lab/Alert";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { Section } from "../../../models/Section";
import { useFormsContext } from "../../../context/FormsContext";
import { INITIAL_VALUES_SECTION } from "../../../models/SectionInitialValues";
import { upsertSection } from "../../../repositories/FormsRepository";
import { ColorPicker } from "./color-picker";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
		"& .MuiSelect-root": {
			height: 40,
			backgroundColor: "unset",
			display: "flex",
			alignItems: "center",
		},
	},
	assetPicker: {
		"& .MuiGrid-item": {
			width: "500px",
		},
	},

	checkbox: {
		userSelect: "none",
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},
	Subtitle: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	data: Section | null;
	newMode?: boolean;
};

export const NewAndEditSectionModal = ({
	show,
	onHide,
	data,
	newMode = false,
}: Props) => {
	const classes = useStyles();
	const {
		state: { FormId, SectionId },
		actions: {
			setSectionNameSelectedInTable,
			setSectionBackgroundColorSelectedInTable,
			setSectionSubtitleSelectedInTable,
			setSectionLayoutParametersSelectedInTable,
		},
	} = useFormsContext();
	const [editedSuccess, setEditedSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const [error, setError] = useState<string>("");
	const [deleteByIdError, setDeleteByIdError] = useState<string>("");
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
		reset,
	} = useForm<Section>({ defaultValues: INITIAL_VALUES_SECTION });

	//states
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditedSuccess(false);
	};
	const handleCancel = async () => {
		reset();
		onHide(false);
	};

	const sectionName = watch("SectionName");

	const changeSectionName = () => {
		setSectionNameSelectedInTable(sectionName);
	};

	const subtitle = watch("Subtitle");

	const changeSubtitle = () => {
		setSectionSubtitleSelectedInTable(subtitle);
	};

	const layoutParameters = watch("LayoutParameters");

	const changeLayoutParameters = () => {
		setSectionSubtitleSelectedInTable(layoutParameters);
	};

	const backgroundColor = watch("BackgroundColor");

	const changeBackgroundColor = () => {
		setSectionBackgroundColorSelectedInTable(backgroundColor);
	};

	const onSubmit = async (sectionData: Section) => {
		setIsSubmitLoading(true);
		const res = await upsertSection(
			FormId,
			SectionId,
			sectionData?.SectionName,
			sectionData?.BackgroundColor as string,
			sectionData?.Subtitle as string,
			sectionData?.LayoutParameters as string,
		);
		if (res.ok) {
			reset();
			setIsSubmitLoading(false);
			setEditedSuccess(true);
			onHide(true);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	useEffect(() => {
		if (data != null && show) {
			reset(INITIAL_VALUES_SECTION);
			setSectionNameSelectedInTable(data.SectionName);
			setSectionBackgroundColorSelectedInTable(data.BackgroundColor);
			setSectionSubtitleSelectedInTable(data.Subtitle);
			setSectionLayoutParametersSelectedInTable(data.LayoutParameters);
			setValue("SectionName", data.SectionName);
			setValue("BackgroundColor", data.BackgroundColor);
			setValue("Subtitle", data.Subtitle);
			setValue("LayoutParameters", data.LayoutParameters);
			setIsSubmitLoading(false);
		} else {
			reset(INITIAL_VALUES_SECTION);
		}
	}, [show]);

	useEffect(() => {
		changeSectionName();
	}, [sectionName]);

	useEffect(() => {
		changeBackgroundColor();
	}, [backgroundColor]);

	useEffect(() => {
		changeSubtitle();
	}, [subtitle]);

	useEffect(() => {
		changeLayoutParameters();
	}, [layoutParameters]);

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container style={{ padding: "15px 0 20px" }}>
						<DialogTitle
							style={{
								padding: "5px 40px 10px 24px",
								marginRight: 20,
								width: "100%",
							}}
						>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<h1 style={{ fontSize: "1.7em" }}>
									{newMode
										? `SECTION: ${sectionName || "New"}`
										: `EDIT: ${data?.SectionName}`}
								</h1>
							</Grid2>
						</DialogTitle>
						<Grid2 container>
							<DialogContent dividers style={{ padding: "0 15px" }}>
								<Grid2 container>
									<Grid2
										style={{ paddingLeft: "10px", marginTop: "-5px" }}
										size={{ md: 12, xs: 12 }}
									>
										<Controller
											name="SectionName"
											control={control}
											rules={{
												validate: (value) => value !== "",
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label="Section Name"
													variant="outlined"
													fullWidth
													error={!!errors.SectionName}
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													className={classes.inputEditable}
												/>
											)}
										/>
									</Grid2>
									<Grid2
										container
										style={{
											justifyContent: "flex-start",
											alignItems: "flex-start",
											marginTop: "20px",
											paddingLeft: "10px",
										}}
										spacing={2}
										size={{ md: 12, xs: 12 }}
									>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<Controller
												name="BackgroundColor"
												control={control}
												render={({ field: { value, onChange } }) => (
													<div style={{ marginTop: "8px" }}>
														<Typography
															variant="caption"
															sx={{
																display: "block",
																color: "rgba(0, 0, 0, 0.6)",
																marginBottom: "8px",
																paddingLeft: "14px",
															}}
														>
															Background Color
														</Typography>
														<ColorPicker
															onChange={onChange}
															color={value as any}
														/>
														{errors.BackgroundColor && (
															<span className={classes.errorLabel}>
																{"Select Color"}
															</span>
														)}
													</div>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<Controller
												name="LayoutParameters"
												control={control}
												render={({ field: { value, onChange } }) => (
													<div style={{ marginTop: "8px" }}>
														<Typography
															variant="caption"
															sx={{
																display: "block",
																color: "rgba(0, 0, 0, 0.6)",
																marginBottom: "8px",
																paddingLeft: "14px",
															}}
														>
															Layout
														</Typography>
														<Select
															value={value}
															onChange={(e) =>
																onChange(e.target.value as string)
															}
															variant="outlined"
															fullWidth
															sx={{
																height: "40px",
																"& .MuiSelect-select": {
																	paddingTop: "10px",
																	paddingBottom: "10px",
																},
															}}
														>
															<MenuItem value="Column=1">Column 1</MenuItem>
															<MenuItem value="Column=2">Column 2</MenuItem>
															<MenuItem value="Column=3">Column 3</MenuItem>
														</Select>
													</div>
												)}
											/>
										</Grid2>
									</Grid2>
									<Grid2
										style={{
											padding: 0,
											marginTop: "20px",
											paddingLeft: "10px",
										}}
										size={{ md: 12, xs: 12 }}
									>
										<Controller
											name="Subtitle"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													label="Subtitle"
													variant="outlined"
													fullWidth
													error={!!errors.Subtitle}
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
													className={classes.inputEditable}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
							</DialogContent>
							<Grid2
								justifyContent="flex-end"
								style={{ marginTop: "15px" }}
								size={{ md: 12, xs: 12 }}
							>
								<DialogActions style={{ padding: "0px 24px" }}>
									<Grid2
										container
										justifyContent="flex-end"
										spacing={2}
										style={{ margin: 0 }}
									>
										<Grid2 size={{ md: 3, xs: 4 }}>
											<Button
												fullWidth
												variant="contained"
												color="inherit"
												onClick={handleCancel}
											>
												Cancel
											</Button>
										</Grid2>
										<Grid2 style={{ paddingRight: 0 }} size={{ md: 3, xs: 4 }}>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save
											</Button>
										</Grid2>
									</Grid2>
								</DialogActions>
							</Grid2>
						</Grid2>
					</Grid2>
				</form>
			</Dialog>
			<Snackbar
				open={editedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={deleteByIdError}
				onHide={() => setDeleteByIdError("")}
			/>
		</>
	);
};
